import React, { useState, useEffect } from 'react';
import { Trophy, Star, Medal, Crown, Target, Flame, Zap, Shield, Heart, BookOpen, Briefcase, Users, Calendar, Award, Gift, ArrowLeft } from 'lucide-react';
import { useDisciplineStore } from '../../contexts/disciplineStore';

const AchievementsComponent = ({ isDarkMode = true, onBackToOverview }) => {
  // Get Digital Discipline data for enhanced achievements
  const { goals, timers, xp, streak, level } = useDisciplineStore();

  // Calculate discipline-based achievements and stats
  const disciplineAchievements = React.useMemo(() => {
    const completedTimers = timers.filter(t => t.isCompleted);
    const totalFocusTime = completedTimers.reduce((total, timer) => total + (timer.duration || 25), 0);
    const completedGoals = goals.filter(g => g.completed);
    
    // Generate dynamic achievements based on actual data
    const dynamicAchievements = [];
    
    // Streak achievements
    if (streak >= 30) {
      dynamicAchievements.push({
        id: 'discipline_streak_30',
        title: 'Discipline Master',
        description: `Maintained a ${streak}-day discipline streak`,
        points: 1000,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'legendary'
      });
    } else if (streak >= 14) {
      dynamicAchievements.push({
        id: 'discipline_streak_14',
        title: 'Consistency Champion',
        description: `Maintained a ${streak}-day discipline streak`,
        points: 500,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'epic'
      });
    } else if (streak >= 7) {
      dynamicAchievements.push({
        id: 'discipline_streak_7',
        title: 'Week Warrior',
        description: `Maintained a ${streak}-day discipline streak`,
        points: 250,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'rare'
      });
    }
    
    // Timer achievements
    if (completedTimers.length >= 100) {
      dynamicAchievements.push({
        id: 'timer_master',
        title: 'Focus Master',
        description: `Completed ${completedTimers.length} focus sessions`,
        points: 800,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'epic'
      });
    } else if (completedTimers.length >= 50) {
      dynamicAchievements.push({
        id: 'timer_expert',
        title: 'Focus Expert',
        description: `Completed ${completedTimers.length} focus sessions`,
        points: 400,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'rare'
      });
    }
    
    // Goal achievements
    if (completedGoals.length >= 20) {
      dynamicAchievements.push({
        id: 'goal_crusher',
        title: 'Goal Crusher',
        description: `Completed ${completedGoals.length} goals`,
        points: 600,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'epic'
      });
    }
    
    // XP achievements
    if (xp >= 5000) {
      dynamicAchievements.push({
        id: 'xp_master',
        title: 'Experience Master',
        description: `Earned ${xp} XP through discipline`,
        points: 750,
        earnedDate: new Date().toISOString().split('T')[0],
        category: 'discipline',
        rarity: 'epic'
      });
    }
    
    return {
      achievements: dynamicAchievements,
      totalFocusHours: Math.round(totalFocusTime / 60),
      completedTimersCount: completedTimers.length,
      completedGoalsCount: completedGoals.length,
      disciplineLevel: level || 1
    };
  }, [goals, timers, xp, streak, level]);

  const [achievementsData, setAchievementsData] = useState({
    totalPoints: 15640 + xp,
    totalBadges: 47 + disciplineAchievements.achievements.length,
    level: Math.max(23, disciplineAchievements.disciplineLevel),
    nextLevelPoints: 1360,
    streaks: {
      current: streak,
      longest: Math.max(42, streak),
      type: 'discipline_streak'
    },
    recentAchievements: [
      ...disciplineAchievements.achievements.slice(0, 3), // Add recent discipline achievements
      {
        id: 1,
        title: 'Health Enthusiast',
        description: 'Logged health data for 30 consecutive days',
        points: 500,
        earnedDate: '2025-05-25',
        category: 'health',
        rarity: 'rare'
      },
      {
        id: 2,
        title: 'Task Master',
        description: 'Completed 100 tasks this month',
        points: 300,
        earnedDate: '2025-05-20',
        category: 'productivity',
        rarity: 'common'
      },
      {
        id: 3,
        title: 'Security Champion',
        description: 'Maintained 100% security score for 2 weeks',
        points: 750,
        earnedDate: '2025-05-15',
        category: 'security',
        rarity: 'epic'
      }
    ].slice(0, 6), // Keep only top 6 achievements
    categories: {
      discipline: {
        name: 'Digital Discipline',
        icon: Target,
        color: 'emerald',
        earnedBadges: disciplineAchievements.achievements.length,
        totalBadges: 15,
        points: xp
      },
      health: {
        name: 'Health & Wellness',
        icon: Heart,
        color: 'red',
        earnedBadges: 12,
        totalBadges: 18,
        points: 4200
      },
      productivity: {
        name: 'Productivity',
        icon: Briefcase,
        color: 'blue',
        earnedBadges: 15,
        totalBadges: 22,
        points: 5100
      },
      security: {
        name: 'Security & Privacy',
        icon: Shield,
        color: 'green',
        earnedBadges: 8,
        totalBadges: 14,
        points: 2800
      },
      learning: {
        name: 'Learning & Growth',
        icon: BookOpen,
        color: 'purple',
        earnedBadges: 7,
        totalBadges: 16,
        points: 2340
      },
      social: {
        name: 'Social & Community',
        icon: Users,
        color: 'indigo',
        earnedBadges: 5,
        totalBadges: 12,
        points: 1200
      }
    }
  });

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [achievementsList, setAchievementsList] = useState([]);

  useEffect(() => {
    // Load achievements based on selected category
    const loadAchievements = () => {
      const allAchievements = [
        // Health Achievements
        {
          id: 'health_1',
          title: 'First Steps',
          description: 'Log your first health entry',
          points: 50,
          category: 'health',
          rarity: 'common',
          earned: true,
          progress: 100,
          requirement: 1,
          current: 1,
          icon: Heart
        },
        {
          id: 'health_2',
          title: 'Weekly Warrior',
          description: 'Complete 7 days of health tracking',
          points: 150,
          category: 'health',
          rarity: 'common',
          earned: true,
          progress: 100,
          requirement: 7,
          current: 7,
          icon: Calendar
        },
        {
          id: 'health_3',
          title: 'Health Enthusiast',
          description: 'Log health data for 30 consecutive days',
          points: 500,
          category: 'health',
          rarity: 'rare',
          earned: true,
          progress: 100,
          requirement: 30,
          current: 30,
          icon: Flame
        },
        {
          id: 'health_4',
          title: 'Fitness Legend',
          description: 'Reach 100,000 total steps',
          points: 1000,
          category: 'health',
          rarity: 'epic',
          earned: false,
          progress: 75,
          requirement: 100000,
          current: 75000,
          icon: Trophy
        },
        // Productivity Achievements
        {
          id: 'prod_1',
          title: 'Getting Started',
          description: 'Complete your first task',
          points: 25,
          category: 'productivity',
          rarity: 'common',
          earned: true,
          progress: 100,
          requirement: 1,
          current: 1,
          icon: Target
        },
        {
          id: 'prod_2',
          title: 'Task Master',
          description: 'Complete 100 tasks this month',
          points: 300,
          category: 'productivity',
          rarity: 'common',
          earned: true,
          progress: 100,
          requirement: 100,
          current: 100,
          icon: Medal
        },
        {
          id: 'prod_3',
          title: 'Focus Master',
          description: 'Complete 50 Pomodoro sessions',
          points: 400,
          category: 'productivity',
          rarity: 'rare',
          earned: false,
          progress: 60,
          requirement: 50,
          current: 30,
          icon: Zap
        },
        // Security Achievements
        {
          id: 'sec_1',
          title: 'Security Conscious',
          description: 'Enable two-factor authentication',
          points: 100,
          category: 'security',
          rarity: 'common',
          earned: true,
          progress: 100,
          requirement: 1,
          current: 1,
          icon: Shield
        },
        {
          id: 'sec_2',
          title: 'Security Champion',
          description: 'Maintain 100% security score for 2 weeks',
          points: 750,
          category: 'security',
          rarity: 'epic',
          earned: true,
          progress: 100,
          requirement: 14,
          current: 14,
          icon: Crown
        },
        // Learning Achievements
        {
          id: 'learn_1',
          title: 'Knowledge Seeker',
          description: 'Read 10 articles',
          points: 200,
          category: 'learning',
          rarity: 'common',
          earned: false,
          progress: 70,
          requirement: 10,
          current: 7,
          icon: BookOpen
        },
        // Social Achievements
        {
          id: 'social_1',
          title: 'Team Player',
          description: 'Share an achievement with friends',
          points: 100,
          category: 'social',
          rarity: 'common',
          earned: false,
          progress: 0,
          requirement: 1,
          current: 0,
          icon: Users
        }
      ];

      if (selectedCategory === 'all') {
        setAchievementsList(allAchievements);
      } else {
        setAchievementsList(allAchievements.filter(a => a.category === selectedCategory));
      }
    };

    loadAchievements();
  }, [selectedCategory]);

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'text-gray-600 bg-gray-100';
      case 'rare': return 'text-blue-600 bg-blue-100';
      case 'epic': return 'text-purple-600 bg-purple-100';
      case 'legendary': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const LevelProgress = () => {
    const progress = ((16000 - achievementsData.nextLevelPoints) / 16000) * 100;
    
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Level Progress</h3>
          <div className="flex items-center space-x-2">
            <Crown className="w-5 h-5 text-yellow-600" />
            <span className="text-xl font-bold text-gray-900">Level {achievementsData.level}</span>
          </div>
        </div>
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress to Level {achievementsData.level + 1}</span>
            <span>{achievementsData.nextLevelPoints} points to go</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">{achievementsData.totalPoints.toLocaleString()}</div>
            <p className="text-sm text-gray-600">Total Points</p>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">{achievementsData.totalBadges}</div>
            <p className="text-sm text-gray-600">Badges Earned</p>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">{achievementsData.streaks.current}</div>
            <p className="text-sm text-gray-600">Current Streak</p>
          </div>
        </div>
      </div>
    );
  };

  const CategoryStats = () => (
    <div className="bg-white rounded-lg p-6 shadow-sm border">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievement Categories</h3>
      <div className="space-y-4">
        {Object.entries(achievementsData.categories).map(([key, category]) => {
          const Icon = category.icon;
          const progress = (category.earnedBadges / category.totalBadges) * 100;
          
          return (
            <div key={key} className="flex items-center space-x-4">
              <div className={`p-2 rounded-lg bg-${category.color}-50`}>
                <Icon className={`w-5 h-5 text-${category.color}-600`} />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-900">{category.name}</span>
                  <span className="text-sm text-gray-600">
                    {category.earnedBadges}/{category.totalBadges}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`bg-${category.color}-500 h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{category.points} points</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const AchievementCard = ({ achievement }) => {
    const Icon = achievement.icon;
    
    return (
      <div className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-md ${
        achievement.earned ? 'bg-white border-green-200' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg ${
            achievement.earned ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            <Icon className={`w-5 h-5 ${
              achievement.earned ? 'text-green-600' : 'text-gray-400'
            }`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <h4 className={`font-medium ${
                achievement.earned ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {achievement.title}
              </h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRarityColor(achievement.rarity)}`}>
                {achievement.rarity}
              </span>
            </div>
            <p className={`text-sm mb-2 ${
              achievement.earned ? 'text-gray-600' : 'text-gray-400'
            }`}>
              {achievement.description}
            </p>
            
            {!achievement.earned && (
              <div className="mb-2">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{achievement.current}/{achievement.requirement}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${achievement.progress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-medium text-gray-900">{achievement.points} points</span>
              </div>
              {achievement.earned && (
                <span className="text-xs text-green-600 font-medium">Earned!</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RecentAchievements = () => (
    <div className="bg-white rounded-lg p-6 shadow-sm border">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h3>
      <div className="space-y-3">
        {achievementsData.recentAchievements.map((achievement) => (
          <div key={achievement.id} className="flex items-center space-x-3 p-3 rounded-lg bg-green-50 border border-green-200">
            <div className="p-2 rounded-lg bg-green-100">
              <Trophy className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRarityColor(achievement.rarity)}`}>
                  {achievement.rarity}
                </span>
              </div>
              <p className="text-sm text-gray-600">{achievement.description}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-500">{achievement.earnedDate}</span>
                <div className="flex items-center space-x-1">
                  <Star className="w-3 h-3 text-yellow-500" />
                  <span className="text-xs font-medium text-gray-900">+{achievement.points}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      {onBackToOverview && (
        <div>
          <button
            onClick={onBackToOverview}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>İstatistiklere Geri Dön</span>
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Achievements & Rewards</h2>        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          <option value="discipline">Digital Discipline</option>
          <option value="health">Health & Wellness</option>
          <option value="productivity">Productivity</option>
          <option value="security">Security & Privacy</option>
          <option value="learning">Learning & Growth</option>
          <option value="social">Social & Community</option>
        </select>
      </div>      {/* Digital Discipline Dashboard */}
      {selectedCategory === 'all' || selectedCategory === 'discipline' ? (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-6 shadow-sm border border-emerald-200">
          <div className="flex items-center space-x-3 mb-6">
            <Target className="w-6 h-6 text-emerald-600" />
            <h3 className="text-lg font-semibold text-emerald-900">Digital Discipline Progress</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{streak}</div>
              <p className="text-sm text-emerald-700">Current Streak</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{disciplineAchievements.completedTimersCount}</div>
              <p className="text-sm text-emerald-700">Focus Sessions</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{disciplineAchievements.totalFocusHours}h</div>
              <p className="text-sm text-emerald-700">Total Focus Time</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{disciplineAchievements.completedGoalsCount}</div>
              <p className="text-sm text-emerald-700">Goals Completed</p>
            </div>
          </div>
          
          {disciplineAchievements.achievements.length > 0 && (
            <div>
              <h4 className="font-medium text-emerald-900 mb-3">Recent Discipline Achievements</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {disciplineAchievements.achievements.slice(0, 4).map((achievement) => (
                  <div key={achievement.id} className="bg-white/60 rounded-lg p-3 border border-emerald-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <Trophy className="w-4 h-4 text-emerald-600" />
                      <span className="font-medium text-emerald-900 text-sm">{achievement.title}</span>
                    </div>
                    <p className="text-xs text-emerald-700">{achievement.description}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-xs text-emerald-600">+{achievement.points} XP</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        achievement.rarity === 'legendary' ? 'bg-yellow-100 text-yellow-800' :
                        achievement.rarity === 'epic' ? 'bg-purple-100 text-purple-800' :
                        achievement.rarity === 'rare' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {achievement.rarity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Top Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <LevelProgress />
        </div>
        <div>
          <CategoryStats />
        </div>
      </div>

      {/* Recent Achievements */}
      <RecentAchievements />

      {/* Achievements Grid */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {selectedCategory === 'all' ? 'All Achievements' : `${achievementsData.categories[selectedCategory]?.name || 'Achievements'}`}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievementsList.map((achievement) => (
            <AchievementCard key={achievement.id} achievement={achievement} />
          ))}
        </div>
      </div>

      {/* Streak Information */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Streaks & Consistency</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-3">
              <Flame className="w-8 h-8 text-orange-600" />
            </div>
            <div className="text-2xl font-bold text-orange-600">{achievementsData.streaks.current}</div>
            <p className="text-sm text-gray-600">Current Streak</p>
            <p className="text-xs text-gray-500">Daily login streak</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-3">
              <Trophy className="w-8 h-8 text-red-600" />
            </div>
            <div className="text-2xl font-bold text-red-600">{achievementsData.streaks.longest}</div>
            <p className="text-sm text-gray-600">Longest Streak</p>
            <p className="text-xs text-gray-500">Personal best</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
              <Gift className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-600">3</div>
            <p className="text-sm text-gray-600">Rewards Available</p>
            <p className="text-xs text-gray-500">Claim your rewards</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementsComponent;
